import { useCallback, useEffect, useState } from 'react';
import './PromoPage.css';

const STORAGE_KEY = 'kya_promo_code';
const CLAIM_ENDPOINT = '/api/claim-promo';

const REDEEM_STEPS = [
  'Open the App Store app on your iPhone or iPad.',
  'Tap your account photo or the sign-in icon in the top-right corner.',
  'Tap "Redeem Gift Card or Code".',
  'Tap "Enter Code Manually".',
  'Enter (or paste) the promo code above, then tap "Redeem".',
  'Know Your Ante? Trivia! will download for free. Enjoy!',
];

function ClipboardIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
      <rect x="8" y="8" width="12" height="13" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
      <path
        d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
      <path
        d="M5 13l4 4L19 7"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function PromoPage() {
  const [status, setStatus] = useState('idle'); // idle | loading | done | empty | error
  const [code, setCode] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(STORAGE_KEY);
      if (saved) {
        setCode(saved);
        setStatus('done');
      }
    } catch (e) {
      /* localStorage unavailable — that's fine, user can still generate a code */
    }
  }, []);

  const generate = useCallback(async () => {
    setStatus('loading');
    try {
      const res = await fetch(CLAIM_ENDPOINT, { method: 'POST' });
      if (res.status === 410) {
        setStatus('empty');
        return;
      }
      if (!res.ok) {
        setStatus('error');
        return;
      }
      const data = await res.json();
      if (!data.code) {
        setStatus('error');
        return;
      }
      setCode(data.code);
      setStatus('done');
      try {
        window.localStorage.setItem(STORAGE_KEY, data.code);
      } catch (e) {
        /* ignore persistence failure */
      }
    } catch (e) {
      setStatus('error');
    }
  }, []);

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      /* clipboard blocked — user can still select the code manually */
    }
  }, [code]);

  return (
    <div className="promo-page">
      <img
        src={process.env.PUBLIC_URL + '/KYA_Lettering_1.png'}
        alt="Know Your Ante?"
        className="promo-logo"
      />

      <div className="promo-card">
        <h1 className="promo-title">Free iOS Download</h1>

        {status !== 'done' && (
          <>
            <p className="promo-subtitle">
              Grab a one-time promo code for <strong>Know Your Ante? Trivia!</strong> on
              the App Store. It's on the house!
            </p>
            <button
              type="button"
              className="promo-button"
              onClick={generate}
              disabled={status === 'loading' || status === 'empty'}
            >
              {status === 'loading' ? 'Dealing your code…' : 'Generate Promo Code'}
            </button>
          </>
        )}

        {status === 'empty' && (
          <p className="promo-message">
            All of the free promo codes have been claimed. Thanks for the
            interest! Maybe ask Jameson or Mark nicely for a code!
          </p>
        )}

        {status === 'error' && (
          <p className="promo-message">
            Something went wrong dealing your code. Please try again in a moment.
          </p>
        )}

        {status === 'done' && (
          <>
            <p className="promo-subtitle">
              Here's your promo code. It's yours alone. Copy it now and redeem it
              in the App Store.
            </p>

            <div className="promo-code-row">
              <span className="promo-code" aria-label={`Promo code ${code}`}>
                {code}
              </span>
              <button
                type="button"
                className={`promo-copy${copied ? ' is-copied' : ''}`}
                onClick={copy}
                aria-label="Copy promo code to clipboard"
              >
                {copied ? <CheckIcon /> : <ClipboardIcon />}
                <span>{copied ? 'Copied' : 'Copy'}</span>
              </button>
            </div>

            <h2 className="promo-steps-title">How to redeem</h2>
            <ol className="promo-steps">
              {REDEEM_STEPS.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>

            <p className="promo-fineprint">
              This code has been pulled from the pile just for you and won't be
              handed to anyone else. Copy it now so you don't lose it.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
